// src/lib/translation/dom-mutation-guard.ts
// Safety net: Chrome's page translator can detach or re-wrap nodes React still
// holds, and removeChild/insertBefore then throw NotFoundError above every
// error boundary. The guard logs the mismatch and carries on instead. The log
// names the operation and node names only: node text can be personal data.

/** A `Node`-shaped value: only what the guard reads. */
export interface NodeLike {
  nodeName: string;
  parentNode: unknown;
}

/** `Node.prototype`-shaped: the three methods the guard wraps or calls. */
export interface NodeProtoLike {
  removeChild(child: NodeLike): unknown;
  insertBefore(node: NodeLike, ref: NodeLike | null): unknown;
  appendChild(node: NodeLike): unknown;
}

const wrapped = new WeakSet<object>();

/** Wraps `removeChild` and `insertBefore` on `proto`. Idempotent. */
export function installDomMutationGuard(proto: NodeProtoLike, log: (message: string) => void): void {
  const { removeChild, insertBefore, appendChild } = proto;
  if (wrapped.has(removeChild) || wrapped.has(insertBefore)) return;

  const guardedRemoveChild = function (this: NodeLike, child: NodeLike) {
    if (child.parentNode !== this) {
      log(`[translation-guard] removeChild: node is not a child of ${this.nodeName}`);
      return child;
    }
    return removeChild.call(this, child);
  };
  const guardedInsertBefore = function (this: NodeLike, node: NodeLike, ref: NodeLike | null) {
    if (ref && ref.parentNode !== this) {
      log(`[translation-guard] insertBefore: ref is not a child of ${this.nodeName}`);
      return appendChild.call(this, node);
    }
    return insertBefore.call(this, node, ref);
  };

  wrapped.add(guardedRemoveChild);
  wrapped.add(guardedInsertBefore);
  proto.removeChild = guardedRemoveChild as NodeProtoLike["removeChild"];
  proto.insertBefore = guardedInsertBefore as NodeProtoLike["insertBefore"];
}
